/** @jsx jsx */
import { Fragment } from 'react';
import { css, jsx } from '@emotion/core';
import { Text } from '@zapier/common-components';
import { Border, Colors, Spacing } from '@zapier/theme';

import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants/url';

import { issuesLink } from 'app/developer-v3/pages/publishing/utils';

const Styles = {
  header: css`
    padding: 20px;
    background-color: ${Colors.status.error.bg};
    border-top-left-radius: ${Border.radius.default};
    border-top-right-radius: ${Border.radius.default};
    h3 {
      padding: ${Spacing.none};
    }
    p {
      margin: ${Spacing.none};
    }
  `,
  body: css`
    border: ${Border.size.default} solid ${Border.color.default};
    border-top: none;
    border-bottom-left-radius: ${Border.radius.default};
    border-bottom-right-radius: ${Border.radius.default};
    padding: 20px;
  `,
  stepsList: css`
    padding-top: 10px;
    & li:first-of-type {
      font-weight: 600;
    }
  `,
};
/**
 * Steps that explain how can a developer resubmit the integration
 * for a follow up review
 *
 * @param integrationId
 */
const stepsToResubmit = (integrationId: number) => [
  <Fragment>
    First, create a new editable version by{' '}
    <Link
      anchor="#clone-your-integration"
      href={`${BASE_DOC_URL_VB}/manage/versions`}
      target="_blank"
    >
      cloning your integration.
    </Link>
  </Fragment>,
  <Fragment>
    Check out the <Link href={issuesLink(integrationId)}>issues page</Link> and
    review the feedback left by the reviewer.
  </Fragment>,
  <Fragment>
    Make any updates to your integration based on our feedback.
  </Fragment>,
  <Fragment>
    Return to the <Link href={issuesLink(integrationId)}>issues page</Link> and
    mark the issues as resolved. Blocking issues are required.
  </Fragment>,
  <Fragment>Submit for review below.</Fragment>,
];

type Props = {
  integrationId: number;
};

const ResubmissionSteps = ({ integrationId }: Props) => (
  <section>
    <header css={Styles.header}>
      <h3>
        <Text>We have some feedback for you</Text>
      </h3>
      <p>
        <Text>
          During our review, we found some issues that we need you to address.
        </Text>
      </p>
    </header>
    <div css={Styles.body}>
      <h3>
        <Text type="section-heading">
          Before you make any updates, review these required steps for
          resubmission.
        </Text>
      </h3>
      <ol css={Styles.stepsList}>
        {stepsToResubmit(integrationId).map((step, i) => (
          <li key={i}>
            <Text>{step}</Text>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export default ResubmissionSteps;
